import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLogout } from "@privy-io/react-auth";
import { Header, Button, Icon, Modal } from "../components/ui";
import { Camera } from "../components/Camera";
import { useStore } from "../store";
import { extractReceiptData } from "../services/ocr";
import { isAndroid } from "../utils/platform";
import { compressImage } from "../utils/image";
import { useToast } from "../hooks/useToast";
import { logScreenView, logOcrAttempted, logOcrSuccess, logOcrFailed, logOcrSkipped, logReceiptRetry } from "../services/analytics";

type CaptureState = "idle" | "scanning" | "done" | "error";

export function ReceiptCaptureScreen() {
  const navigate = useNavigate();
  const [captureState, setCaptureState] = useState<CaptureState>("idle");
  const [localImage, setLocalImage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const toast = useToast();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const { setCapturedImage, setOCRLoading, setOCRSuccess, setOCRError, resetAll, extendSession, app } =
    useStore();

  const allowedMethods = app.venue?.allowedMethods ?? [];
  const allowOcr = allowedMethods.includes('ocr');
  const allowManual = allowedMethods.includes('manual');

  const { logout } = useLogout();

  useEffect(() => {
    // If only manual entry is allowed, skip capture screen entirely
    if (!allowOcr && allowManual) {
      navigate('/confirm', { state: { manualEntry: true }, replace: true });
      return;
    }
    extendSession()
    logScreenView('receipt_capture')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const processFile = async (file: File) => {
    const imageUrl = URL.createObjectURL(file);
    setLocalImage(imageUrl);
    setCapturedImage(imageUrl);
    setCaptureState("scanning");
    setOCRLoading();
    setErrorMessage(null);

    try {
      const venueId = useStore.getState().getVenueId();
      if (!venueId) {
        throw new Error("Venue session expired or not found");
      }
      logOcrAttempted(venueId);
      const result = await extractReceiptData(file, venueId);
      setOCRSuccess(result);
      logOcrSuccess(venueId);
      setCaptureState("done");

      // Auto-navigate after showing success briefly
      setTimeout(() => {
        navigate("/confirm");
      }, 800);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to scan receipt";
      const venueId = useStore.getState().getVenueId();
      if (venueId) logOcrFailed(venueId, message);
      setErrorMessage(message);
      setOCRError(message);
      setCaptureState("error");
      toast.error(message);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setCaptureState("scanning"); // Show loading state early
      const compressedFile = await compressImage(file);
      await processFile(compressedFile);
    } catch (err) {
      console.error("Compression failed, using original", err);
      await processFile(file);
    }
  };

  const handleCameraCapture = async (file: File) => {
    setShowCamera(false);
    await processFile(file);
  };

  const handleCameraClick = () => {
    if (isAndroid()) {
      setShowCamera(true);
    } else {
      cameraInputRef.current?.click();
    }
  };

  const handleGalleryClick = () => {
    galleryInputRef.current?.click();
  };

  const handleRetake = () => {
    const venueId = useStore.getState().getVenueId();
    if (venueId) logReceiptRetry(venueId);
    setLocalImage(null);
    setCapturedImage(null);
    setCaptureState("idle");
    setErrorMessage(null);
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };



  const handleManualEntry = () => {
    const venueId = useStore.getState().getVenueId();
    if (venueId) logOcrSkipped(venueId);
    // Navigate with manual entry flag
    navigate("/confirm", { state: { manualEntry: true } });
  };

  const getTitle = () => {
    switch (captureState) {
      case "idle":
        return "Capture Receipt";
      case "scanning":
        return "Scanning Receipt...";
      case "done":
        return "Receipt Scanned!";
      case "error":
        return "Scan Failed";
    }
  };

  const getSubtitle = () => {
    switch (captureState) {
      case "idle":
        return "Take a photo of your receipt";
      case "scanning":
        return "Extracting details from your receipt";
      case "done":
        return "We found your total";
      case "error":
        return errorMessage || "Something went wrong";
    }
  };

  // Show in-browser camera for Android
  if (showCamera) {
    return (
      <Camera
        onCapture={handleCameraCapture}
        onClose={() => setShowCamera(false)}
      />
    );
  }

  return (
    <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
      {/* Hidden file inputs - camera input only used on iOS */}
      <input
        data-testid="camera-input"
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <Header
        showLogo
        rightAction={
          <button
            onClick={() => setShowLogoutModal(true)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
          >
            <Icon name="logout" size={22} className="text-[var(--color-text-muted)]" />
          </button>
        }
      />

      <Modal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Sign out?"
        confirmLabel="Sign out"
        onConfirm={() => {
          resetAll()
          logout()
          navigate("/")
        }}
        variant="danger"
        icon="logout"
      >
        Are you sure you want to sign out of your account?
      </Modal>

      {/* Main Content */}
      <main className="flex-1 flex flex-col px-6 pb-8">
        {/* Title */}
        <div className="mt-4 mb-6 text-center">
          <h1 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
            {getTitle()}
          </h1>
          <p className="text-[var(--color-text-muted)] text-sm">
            {getSubtitle()}
          </p>
        </div>

        {/* Preview Area */}
        <div className="flex-1 relative mb-6">
          <div className="absolute inset-0 bg-[var(--color-surface)] rounded-2xl overflow-hidden border border-[var(--color-border)] shadow-sm">
            {!localImage ? (
              /* Empty state */
              <div className="w-full h-full flex flex-col items-center justify-center text-[var(--color-text-light)]">
                <Icon
                  name="document"
                  size={64}
                  className="mb-4 opacity-50"
                  strokeWidth={1}
                />
                <span className="text-sm">Your receipt will appear here</span>
              </div>
            ) : (
              /* Captured image with scanning animation */
              <div className="relative w-full h-full">
                <img
                  src={localImage}
                  alt="Captured receipt"
                  className="w-full h-full object-contain"
                />

                {/* Scanning line animation */}
                {captureState === "scanning" && (
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="scan-line absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[var(--color-secondary)] to-transparent shadow-[0_0_15px_var(--color-secondary)]" />
                  </div>
                )}

                {/* Success overlay */}
                {captureState === "done" && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[var(--color-success)] flex items-center justify-center">
                      <Icon
                        name="check"
                        size={40}
                        className="text-white"
                        strokeWidth={3}
                      />
                    </div>
                  </div>
                )}

                {/* Error overlay */}
                {captureState === "error" && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[var(--color-error)] flex items-center justify-center">
                      <span className="text-white text-4xl font-bold">!</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Controls */}
        {captureState === "idle" && (
          <div className="space-y-4">
            <Button onClick={handleCameraClick}>
              <Icon name="camera" size={24} />
              Take Photo
            </Button>

            <Button variant="outline" onClick={handleGalleryClick}>
              <Icon name="image" size={20} />
              Choose from Gallery
            </Button>

            {allowManual && (
              <button
                onClick={handleManualEntry}
                className="w-full py-3 text-[var(--color-text-muted)] font-medium text-sm"
              >
                Enter amount manually
              </button>
            )}
          </div>
        )}

        {captureState === "scanning" && (
          <div className="text-center py-4">
            <p className="text-[var(--color-text-muted)] text-sm animate-pulse">
              Please wait...
            </p>
          </div>
        )}

        {captureState === "done" && (
          <div className="text-center py-4">
            <p className="text-[var(--color-text-muted)] text-sm">
              Redirecting...
            </p>
          </div>
        )}

        {captureState === "error" && (
          <div className="space-y-4">
            <Button onClick={handleRetake}>Try Again</Button>

            {allowManual && (
              <Button variant="outline" onClick={handleManualEntry}>
                Enter Amount Manually
              </Button>
            )}
          </div>
        )}


      </main>
    </div>
  );
}
